import React from "react";
import {
  Box,
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  Button,
  Divider,
  useColorModeValue,
  Flex,
  Badge,
  Icon,
  Switch,
} from "@chakra-ui/react";
import ToggleSwitch from "./ToggleSwitch";

const CustomDataCard = ({
  payStatus,
  // Title props
  title = "",
  titleColor = "gray.800",
  titleSize = "lg",
  titleWeight = "bold",

  // Subtitle props
  subtitle,
  subtitleColor = "gray.600",
  subtitleSize = "sm",
  subtitleWeight = "normal",

  // Data props
  data = [],
  keyWidth = "140px",
  keyColor = "gray.700",
  keySize = "sm",
  keyWeight = "medium",
  valueColor = "gray.600",
  valueSize = "sm",
  valueWeight = "normal",
  valueAlign = "left",

  // Button props
  showButton = false,
  buttonText = "View Details",
  buttonVariant = "solid",
  buttonColorScheme = "blue",
  buttonSize = "sm",
  onButtonClick,
  buttonPosition = "bottom", // "bottom" or "header"

  // Card styling props
  cardBg,
  borderColor,
  borderWidth = "1px",
  borderRadius = "2xl",
  shadow = "md",
  padding = 6,
  spacing = 3,

  // Header styling
  headerIcon,
  headerBg,
  headerPadding = 0,

  // Additional props
  maxItems,
  showDividers = true,
  hoverEffect = true,
  isToggleShow = false,
  isOn = false,
  handleToggle,
  isBlkLpg = false,
  ...props
}) => {
  const defaultCardBg = useColorModeValue("white", "gray.800");
  const defaultBorderColor = useColorModeValue("gray.200", "gray.700");
  const defaultTitleColor = useColorModeValue("gray.800", "white");
  const defaultSubtitleColor = useColorModeValue("gray.600", "gray.400");
  const defaultKeyColor = useColorModeValue("gray.700", "gray.300");
  const defaultValueColor = useColorModeValue("gray.600", "gray.400");
  const dividerColor = useColorModeValue("gray.100", "gray.700");

  const displayData = maxItems ? data.slice(0, maxItems) : data;

  const cleanValue = (value) => {
    if (React.isValidElement(value)) return value;
    if (
      !value ||
      value.toString().trim() === "" ||
      value.toString().trim().match(/^\s+$/)
    ) {
      return "Not provided";
    }
    return value.toString().trim();
  };

  return (
    <Card
      bg={isBlkLpg ? "#faf5ff" : (cardBg || defaultCardBg)}
      shadow={isBlkLpg ? "0 4px 20px rgba(128, 0, 255, 0.15)" : shadow}
      borderRadius={borderRadius}
      border={isBlkLpg ? "1.5px solid #c084fc" : `${borderWidth} solid ${borderColor || defaultBorderColor}`}
      overflow="hidden"
      maxW="420px"
      w="100%"
      minH="450px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      transition="all 0.3s ease"
      _hover={
        hoverEffect
          ? {
              shadow: isBlkLpg ? "0 8px 30px rgba(128, 0, 255, 0.25)" : "xl",
              transform: "translateY(-4px)",
            }
          : {}
      }
      {...props}
    >
      {isBlkLpg && (
        <Box
          bgGradient="linear(to-r, #7c3aed, #a855f7, #7c3aed)"
          px={4}
          py={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <HStack spacing={2}>
            <Text fontSize="13px" fontWeight="700" color="white" letterSpacing="1.5px" textTransform="uppercase">
              Bulk LPG Order
            </Text>
          </HStack>
          <Badge
            bg="whiteAlpha.300"
            color="white"
            fontSize="10px"
            fontWeight="600"
            px={2}
            py={0.5}
            borderRadius="full"
            letterSpacing="0.5px"
          >
            Bulk
          </Badge>
        </Box>
      )}
      <CardBody
        p={padding}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <VStack spacing={spacing} align="stretch" flex={1}>
          <Text
            fontSize={18}
            fontWeight={"bold"}
            color={payStatus == "Paid" ? "green" : "red"}
            noOfLines={1}
          >
            {payStatus}
          </Text>
          {/* Header Section */}
          {isToggleShow && (
            <div
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                display: "flex",
                gap: 8,
              }}
            >
              <span>Auto Assigning</span>

              <div
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  display: "flex",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: "initial" }}>
                  Manual
                </span>
                <ToggleSwitch isOn={isOn} handleToggle={handleToggle} />
                <span style={{ fontSize: 12, fontWeight: "initial" }}>
                  Auto
                </span>
              </div>
            </div>
          )}
          {(title || subtitle) && (
            <Flex justify="space-between" align="center" mb={4}>
              <Box>
                <HStack spacing={2}>
                  {headerIcon && (
                    <Icon as={headerIcon} color="blue.500" boxSize={5} />
                  )}
                  {title && (
                    <Text
                      fontSize={titleSize}
                      fontWeight={titleWeight}
                      color={titleColor || defaultTitleColor}
                    >
                      {title}
                    </Text>
                  )}
                </HStack>
                {subtitle && (
                  <Text
                    fontSize={subtitleSize}
                    fontWeight={subtitleWeight}
                    color={subtitleColor || defaultSubtitleColor}
                    mt={1}
                  >
                    {subtitle}
                  </Text>
                )}
              </Box>

              {showButton && buttonPosition === "header" && (
                <Button
                  size={buttonSize}
                  variant={buttonVariant}
                  colorScheme={buttonColorScheme}
                  onClick={onButtonClick}
                >
                  {buttonText}
                </Button>
              )}
            </Flex>
          )}

          {displayData.length > 0 && <Divider borderColor={dividerColor} />}

          {/* Data Section */}
          {displayData.length > 0 && (
            <VStack
              spacing={3}
              align="stretch"
              flex={1}
              justify="flex-start"
              minH="300px"
            >
              {displayData.map((item, index) => {
                const key = item.key || item.label || item.name;
                const value = item.value || item.data || item.text;
                const cleanedValue = cleanValue(value);
                const status = value == "Paid" ? true : false;

                return (
                  <React.Fragment key={index}>
                    <Flex justify="space-between" align="flex-start" gap={2}>
                      <Box
                        minW={keyWidth}
                        textAlign="left"
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                      >
                        <Text
                          fontSize={item.keySize || keySize}
                          fontWeight={item.keyWeight || keyWeight}
                          color={item.keyColor || keyColor || defaultKeyColor}
                          noOfLines={1}
                        >
                          {key} :
                        </Text>
                      </Box>

                      <Flex
                        flex={1}
                        justify={valueAlign}
                        align="flex-start"
                        gap={2}
                        flexWrap="wrap"
                      >
                        {React.isValidElement(cleanedValue) ? (
                          cleanedValue
                        ) : (
                          <Text
                            fontSize={item.valueSize || valueSize}
                            fontWeight={item.valueWeight || valueWeight}
                            color={
                              item.valueColor || valueColor || defaultValueColor
                            }
                            textAlign={valueAlign}
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflow="visible"
                            opacity={cleanedValue === "Not provided" ? 0.6 : 1}
                            fontStyle={
                              cleanedValue === "Not provided"
                                ? "italic"
                                : "normal"
                            }
                          >
                            {cleanedValue}
                          </Text>
                        )}

                        {item.badge && (
                          <Badge
                            colorScheme={item.badgeColorScheme || "blue"}
                            variant={item.badgeVariant || "subtle"}
                            fontSize="0.7em"
                          >
                            {item.badge}
                          </Badge>
                        )}

                        {item.icon && (
                          <Icon
                            as={item.icon}
                            color={item.iconColor || "gray.400"}
                            boxSize={item.iconSize || 4}
                          />
                        )}
                      </Flex>
                    </Flex>

                    {showDividers && index < displayData.length - 1 && (
                      <Divider borderColor={dividerColor} />
                    )}
                  </React.Fragment>
                );
              })}
            </VStack>
          )}

          {/* Bottom Button */}
          {showButton && buttonPosition === "bottom" && (
            <>
              {displayData.length > 0 && <Divider borderColor={dividerColor} />}
              <Flex justify="center" mt={3} mb={1}>
                <Button
                  size={buttonSize}
                  variant={buttonVariant}
                  colorScheme={buttonColorScheme}
                  onClick={onButtonClick}
                >
                  {buttonText}
                </Button>
              </Flex>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default CustomDataCard;
